import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMG } from "@/veedu/data";
import { ChapterLabel, FadeUp, MotionBackgroundVideo } from "./Shared";

const QUOTE_A = "Some recipes are not written.";
const QUOTE_B = "They are remembered";

const playlist = [
  "/videos/hospitality.mp4",
  "/videos/hospitality2.mp4",
  "/videos/hospitality3.mp4"
];

const Courtyard = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const [activeIdx, setActiveIdx] = useState(0);

  return (
    <section
      id="kitchen"
      data-chapter
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div className="absolute inset-0 -top-[10%] h-[120%]" style={{ y: bgY }}>
        {playlist.map((src, idx) => (
          <MotionBackgroundVideo
            key={src}
            className="absolute inset-0 h-full w-full object-cover"
            src={src}
            poster={idx === 0 ? IMG.backwaters : undefined}
            autoPlay={activeIdx === idx}
            loop={false}
            onEnded={() => setActiveIdx((prev) => (prev + 1) % playlist.length)}
            initial={{ opacity: 0 }}
            animate={activeIdx === idx ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          />
        ))}
      </motion.div>
      <div className="absolute inset-0 bg-ink/65" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-transparent to-ink" />

      <div className="relative z-10 w-full px-6 md:pl-[12%] lg:pl-[14%] xl:pl-[16%] pr-6 md:pr-12 lg:pr-16 py-8 md:py-12">
        <ChapterLabel num="05" name="The Kitchen" mal="അടുക്കള" />
        <h2 data-testid="courtyard-quote" className="mt-10 md:mt-16 font-display text-ivory">
          <span className="block max-w-4xl text-3xl leading-[1.1] tracking-tight sm:text-5xl lg:text-7xl">
            <FadeUp delay={0.1}>{QUOTE_A}</FadeUp>
          </span>
          <span className="block max-w-4xl text-3xl leading-[1.1] tracking-tight sm:text-5xl lg:text-7xl mt-2">
            <FadeUp delay={0.35}>
              <em className="text-brass">{QUOTE_B}</em>
            </FadeUp>
          </span>
        </h2>
        <FadeUp delay={0.5} className="mt-8 md:mt-14 max-w-xl space-y-4 font-body">
          <p className="italic text-xs sm:text-sm font-light leading-relaxed text-cream/85 md:text-base">
            Within these centuries-old walls, a new story is being told. VEEDU brings the flavours of Jomon’s Kerala home into one of Hornchurch’s historic timber-framed buildings — two cultures, generations apart, meeting under one roof.
          </p>
          <p className="italic text-xs sm:text-sm font-light leading-relaxed text-cream/85 md:text-base">
            Here, recipes shaped by memory, family and tradition find a new home. Spices are ground, stories are shared, and every plate carries something of the <em className="text-brass font-medium">naadu</em> it came from.
          </p>
          <p className="italic text-xs sm:text-sm font-semibold leading-relaxed text-brass md:text-base">
            This is more than a kitchen.<br />
            It is Kerala remembered, in a house that remembers too.
          </p>
        </FadeUp>
      </div>
    </section>
  );
};

export default Courtyard;
